import { z } from 'zod';

export const educationModuleContextSchema = z.object({
  kind: z.enum(['registration', 'onboarding_assignment', 'training_program', 'direct_assignment']),
  id: z.string().min(1),
});

export const educationModuleTargetSchema = z.object({
  profile_ref: z.string().min(1),
  module_id: z.string().min(1),
  initial_slide_id: z.string().min(1).optional(),
  context: educationModuleContextSchema,
  em_log_id: z.string().min(1).optional(),
});

export const educationModuleMediaSchema = z.object({
  state: z.enum(['ready', 'unavailable']),
  asset_id: z.string().nullable(),
  url: z.url().nullable(),
  content_type: z.string().nullable(),
  alt: z.string().nullable(),
  expires_at: z.iso.datetime().nullable(),
  refresh: z.literal('slide'),
});

const elementIdentity = { id: z.string().nullable(), index: z.number().nullable() };

export const educationModuleElementSchema = z.discriminatedUnion('type', [
  z.object({ ...elementIdentity, type: z.literal('text'), text: z.string() }),
  z.object({ ...elementIdentity, type: z.literal('rich_text'), html: z.string() }),
  z.object({
    ...elementIdentity,
    type: z.literal('title_and_subtitle'),
    title: z.string().nullable(),
    subtitle: z.string().nullable(),
    style: z.string().nullable(),
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('image'),
    media: educationModuleMediaSchema,
    link_url: z.url().nullable(),
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('video'),
    media: educationModuleMediaSchema,
    poster: educationModuleMediaSchema,
    captions: educationModuleMediaSchema.nullable(),
    transcript: z.string().nullable(),
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('background_image'),
    media: educationModuleMediaSchema,
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('quote'),
    text: z.string(),
    source: z.string().nullable(),
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('link'),
    label: z.string(),
    url: z.url(),
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('activity_action'),
    activity_key: z.enum(['online_module', 'online_exam']),
    state: z.enum(['available', 'blocked', 'unavailable']),
    display: z.object({ label: z.string().min(1) }),
    permitted_actions: z.array(z.enum(['open', 'review'])),
    activity_target: z
      .object({
        kind: z.literal('education_module'),
        profile_ref: z.string().min(1),
        module_id: z.string().min(1),
        mode: z.enum(['module', 'exam']),
        context: z.object({ kind: z.literal('registration'), id: z.string().min(1) }),
        initial_slide_id: z.string().min(1),
        em_log_id: z.string().min(1).nullable(),
      })
      .nullable(),
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('document_action'),
    document_id: z.string().min(1).nullable(),
    document_type: z.enum([
      'medical_questionnaire',
      'liability_release',
      'course_completion_form',
      'paper_completion_form',
      'certificate',
    ]),
    state: z.enum(['not_started', 'draft', 'in_review', 'approved', 'not_valid', 'unavailable']),
    context: educationModuleContextSchema,
    display: z.object({ label: z.string().min(1) }),
    permitted_actions: z.array(
      z.enum(['provision', 'read', 'update_responses', 'submit', 'review', 'upload'])
    ),
    upload_metadata: z
      .object({
        type: z.literal('paper-completion-form'),
        userProfileId: z.uuid(),
        registrationId: z.uuid(),
        certificationTypeId: z.uuid(),
      })
      .optional(),
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('exam_result'),
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('exam_question'),
    question_id: z.string().min(1),
    prompt: z.string(),
    answer_mode: z.enum(['single', 'multiple']),
    media: educationModuleMediaSchema.nullable(),
    answers: z.array(
      z.object({
        answer_id: z.string().min(1),
        index: z.number().nullable(),
        label: z.string(),
        media: educationModuleMediaSchema.nullable(),
      })
    ),
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('instructor_bios'),
    locale: z.object({
      requested: z.string().min(1),
      resolved: z.string().min(1),
      fallback_used: z.boolean(),
    }),
    course_title: z.string().nullable(),
    instructors: z.array(
      z.object({
        instructor_id: z.string().min(1),
        role: z.enum(['lead', 'co_instructor']),
        display_name: z.string().min(1),
        bio: z.string().nullable(),
        photo: educationModuleMediaSchema,
        contact: z.object({
          phone: z.string().nullable(),
          whatsapp: z.string().nullable(),
          telegram: z.string().nullable(),
        }),
      })
    ),
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('course_location_and_schedule'),
    locale: z.object({
      requested: z.string().min(1),
      resolved: z.string().min(1),
      fallback_used: z.boolean(),
    }),
    course_id: z.string().min(1),
    title: z.string().nullable(),
    location_text: z.string().nullable(),
    starts_at: z.iso.datetime().nullable(),
    ends_at: z.iso.datetime().nullable(),
    description: z.string().nullable(),
  }),
  z.object({
    ...elementIdentity,
    type: z.literal('contextual'),
    context_type: z.string().min(1),
    state: z.literal('unavailable'),
    message: z.string().min(1),
  }),
]);

const localeSchema = z.object({
  requested: z.string().min(1),
  resolved: z.string().min(1),
  fallback_used: z.boolean(),
});

export const educationModuleSchema = z.object({
  id: z.string().min(1),
  type: z.enum(['content', 'exam', 'other']),
  title: z.string(),
  description: z.string().nullable(),
  publication: z.object({
    state: z.enum(['published', 'assigned']),
    version: z.string().nullable(),
  }),
  locale: localeSchema,
  policy: z.object({ maximum_attempts: z.number().int().positive().nullable() }),
  slides: z.array(
    z.object({
      id: z.string().min(1),
      index: z.number().int(),
      title: z.string().nullable(),
      duration_seconds: z.number().nonnegative().nullable(),
    })
  ),
});

export const educationModuleSlideSchema = z.object({
  id: z.string().min(1),
  module_id: z.string().min(1),
  index: z.number().int(),
  title: z.string().nullable(),
  type: z.string().nullable(),
  duration_seconds: z.number().nonnegative().nullable(),
  locale: localeSchema,
  navigation: z.object({
    previous_slide_id: z.string().nullable(),
    next_slide_id: z.string().nullable(),
    is_first: z.boolean(),
    is_last: z.boolean(),
  }),
  cover: educationModuleMediaSchema,
  elements: z.array(educationModuleElementSchema),
});

const assessmentRevealSchema = z.object({ state: z.enum(['hidden', 'available']) });

export const educationModuleLogSchema = z.object({
  id: z.string().min(1),
  profile_ref: z.string().min(1),
  module_id: z.string().min(1),
  context: educationModuleContextSchema,
  run_number: z.number().int().positive(),
  state: z.enum(['in_progress', 'finalizing', 'passed', 'failed', 'completed', 'abandoned']),
  revision: z.number().int().positive(),
  started_at: z.iso.datetime(),
  completed_at: z.iso.datetime().nullable(),
  attempt_policy: z.object({
    attempt_number: z.number().int().positive(),
    attempts_used: z.number().int().nonnegative(),
    maximum_attempts: z.number().int().positive().nullable(),
  }),
  assessment_reveal: assessmentRevealSchema,
  score: z
    .object({
      points: z.number(),
      points_possible: z.number(),
      basis_points: z.number().int(),
    })
    .nullable(),
  result: z.string().nullable(),
  permitted_actions: z.array(z.enum(['update_slide', 'submit_question', 'complete', 'review'])),
  slide_logs: z.array(
    z.object({
      slide_id: z.string().min(1),
      index: z.number().int(),
      state: z.enum(['not_started', 'in_progress', 'completed', 'skipped']),
    })
  ),
  question_logs: z.array(
    z.object({
      question_id: z.string().min(1),
      index: z.number().int(),
      submission_count: z.number().int().nonnegative(),
      maximum_submissions: z.number().int().positive(),
      state: z.enum(['unanswered', 'submitted']),
      assessment_reveal: assessmentRevealSchema,
      result: z.enum(['correct', 'incorrect', 'partial']).optional(),
      points_earned: z.number().optional(),
    })
  ),
});

export const educationModuleResponseSchema = z.object({ data: educationModuleSchema });
export const educationModuleSlideResponseSchema = z.object({ data: educationModuleSlideSchema });
export const educationModuleLogResponseSchema = z.object({ data: educationModuleLogSchema });
export const educationModuleLogListResponseSchema = z.object({
  data: z.array(educationModuleLogSchema),
});

export type EducationModuleContext = z.infer<typeof educationModuleContextSchema>;
export type EducationModuleTarget = z.infer<typeof educationModuleTargetSchema>;
export type EducationModule = z.infer<typeof educationModuleSchema>;
export type EducationModuleSlide = z.infer<typeof educationModuleSlideSchema>;
export type EducationModuleElement = z.infer<typeof educationModuleElementSchema>;
export type EducationModuleMedia = z.infer<typeof educationModuleMediaSchema>;
export type EducationModuleLog = z.infer<typeof educationModuleLogSchema>;
